import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";

import apiClient from "../../../../../../auth/apiClient";
import InstallationPhotoService from "../../../../../../services/InstallationPhotoService";
import { useInstallationImage } from "./useInstallationImage";
import { useStyles } from "./styles";

const service = new InstallationPhotoService(apiClient);

/**
 * Full-screen single-photo viewer (Requirement 5).
 *
 * - Lazily fetches the full-resolution image (base64) on open via the service,
 *   with a loading indicator (Req 5.1, 5.2).
 * - CLOSE control returns to the list (Req 5.3).
 * - Delete (trash) control triggers onDelete; the yes/no confirmation is
 *   handled by the container via the shared confirmation dialog (Req 5.4-5.9).
 * - Handles image load failure with a message.
 *
 * Rendered through a portal so the fixed overlay escapes any transformed
 * ancestor and covers the true viewport.
 */
const PhotoViewer = ({ photo, onClose, onDelete, deleting, deleteError }) => {
  const classes = useStyles();
  const { src, loading, error } = useInstallationImage(
    service,
    photo && photo.deviceId,
    photo && photo.installationImageId
  );

  if (!photo) return null;

  return ReactDOM.createPortal(
    <Box className={classes.viewerContainer} data-testid="photo-viewer">
      <Box className={classes.viewerImageArea}>
        {loading && (
          <Box className={classes.viewerLoading} data-testid="viewer-loading">
            <CircularProgress />
          </Box>
        )}
        {!loading && error && (
          <Box className={classes.viewerError} data-testid="viewer-load-error">
            <Typography color="error" variant="body2">
              The image could not be loaded.
            </Typography>
          </Box>
        )}
        {!loading && !error && src && (
          <img
            className={classes.viewerImage}
            src={src}
            alt="Installation"
            data-testid="viewer-image"
          />
        )}
      </Box>

      {deleteError && (
        <Typography
          color="error"
          variant="body2"
          className={classes.inlineError}
          data-testid="viewer-delete-error"
        >
          {deleteError}
        </Typography>
      )}

      <Box className={classes.viewerActions}>
        <Button
          variant="contained"
          onClick={onClose}
          className={classes.closeButton}
          data-testid="viewer-close-button"
        >
          CLOSE
        </Button>
        <IconButton
          aria-label="delete photo"
          onClick={() => onDelete(photo)}
          disabled={deleting}
          className={classes.viewerDeleteButton}
          data-testid="viewer-delete-button"
        >
          {deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
        </IconButton>
      </Box>
    </Box>,
    document.body
  );
};

PhotoViewer.propTypes = {
  photo: PropTypes.shape({
    installationImageId: PropTypes.string,
    deviceId: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  deleting: PropTypes.bool,
  deleteError: PropTypes.string,
};

PhotoViewer.defaultProps = {
  photo: null,
  deleting: false,
  deleteError: null,
};

export default PhotoViewer;
